import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { RefreshCw, ChevronRight } from "lucide-react";
import { api, brl, apiError } from "@/lib/api";
import { TopBar, NotificationBell } from "@/components/Shell";
import { toast } from "sonner";

const LABEL = {
  pending: "Recebido", accepted: "Aceito", preparing: "Em preparo", ready: "Pronto",
  picked_up: "A caminho", delivered: "Entregue", rejected: "Recusado", cancelled: "Cancelado",
};
const COLOR = {
  delivered: "#2E8B57", rejected: "#E53238", cancelled: "#E53238", picked_up: "#FF7A1A",
};

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const navigate = useNavigate();

  const load = () => api.get("/orders").then(({ data }) => setOrders(data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const repeat = async (id) => {
    try {
      const { data } = await api.post(`/orders/${id}/repeat`);
      toast.success("Pedido repetido com sucesso!");
      navigate(`/pedido/${data.id}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="orders-page">
      <TopBar subtitle="Histórico de pedidos" right={<NotificationBell />} />
      <div className="px-4 pt-5">
        <h1 className="font-display text-2xl font-extrabold">Meus pedidos</h1>
        <div className="grid gap-3 mt-4 gf-stagger" data-testid="orders-list">
          {orders.map((o) => (
            <div key={o.id} className="rounded-2xl border border-gray-200 p-4 bg-white">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-display font-bold">{o.store_name}</p>
                  <p className="text-[11px] text-gray-500">
                    {o.code} · {new Date(o.created_at).toLocaleDateString("pt-BR")} ·{" "}
                    {o.items.reduce((a, i) => a + i.qty, 0)} itens
                  </p>
                </div>
                <span
                  data-testid={`order-status-${o.id}`}
                  className="text-[10px] font-bold px-2 py-1 rounded-full text-white"
                  style={{ background: COLOR[o.status] || "#141414" }}
                >
                  {LABEL[o.status]}
                </span>
              </div>
              <p className="font-display font-extrabold text-gf-orange mt-2">{brl(o.total)}</p>
              <div className="flex gap-2 mt-3">
                <Link
                  to={`/pedido/${o.id}`}
                  data-testid={`track-order-${o.id}`}
                  className="flex-1 text-center text-xs font-bold py-2.5 rounded-xl bg-gf-gray gf-press flex items-center justify-center gap-1"
                >
                  Acompanhar <ChevronRight size={14} />
                </Link>
                {["pending", "accepted", "preparing", "ready"].includes(o.status) ? (
                  <button
                    onClick={async () => {
                      const reason = window.prompt("Motivo do cancelamento (opcional):") ?? "";
                      try {
                        await api.post(`/orders/${o.id}/cancel`, { reason });
                        toast.success("Pedido cancelado");
                        load();
                      } catch (e) {
                        toast.error(apiError(e.response?.data?.detail));
                      }
                    }}
                    data-testid={`cancel-order-${o.id}`}
                    className="flex-1 text-xs font-bold py-2.5 rounded-xl border border-mage-red text-mage-red gf-press"
                  >
                    Cancelar pedido
                  </button>
                ) : (
                  <button
                    onClick={() => repeat(o.id)}
                    data-testid={`repeat-order-${o.id}`}
                    className="flex-1 text-xs font-bold py-2.5 rounded-xl gf-gradient text-white gf-press flex items-center justify-center gap-1"
                  >
                    <RefreshCw size={13} /> Repetir pedido
                  </button>
                )}
              </div>
            </div>
          ))}
          {orders.length === 0 && <p className="text-sm text-gray-500 py-10 text-center">Você ainda não fez pedidos.</p>}
        </div>
      </div>
    </div>
  );
}
